import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Alert, FlexColumn, FlexRow, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {GroupByControl} from './GroupByControl';
import {ColumnManager} from './ColumnManager';
import {EmptyReportState} from './EmptyReportState';
import {ReportErrorState} from './ReportErrorState';
import {ReportTable} from './ReportTable/ReportTable';
import {useClientGrouping} from './ReportTable/useClientGrouping';
import {AIBanner} from './TopBar/AIBanner';
import {EditTopBar} from './TopBar/EditTopBar';
import {ReportPageHeader} from './TopBar/ReportPageHeader';
import {ViewTopBar} from './TopBar/ViewTopBar';

const tokens = tokensForThemeProvider.sky.sys;

/** Horizontal page gutter (32px in the design's 1920px frame) and vertical rhythm so the page header,
 * controls row, AI banner, and table are all inset from the viewport edges and evenly spaced,
 * rather than full-bleed — see the design. the design's `Page Header`
 * container has asymmetric vertical padding
 * (`paddingTop=8 paddingBottom=16`, not a uniform 16) and `itemSpacing=16` between its children
 * — not the 12px gap previously used here. */
const SurfaceWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['400']};
	width: 100%;
	padding: ${tokens.spacing['200']} ${tokens.spacing['800']} ${tokens.spacing['400']};
`;

/** Assembled AI Report Builder surface body: the page header (Reports eyebrow + report name
 * heading/pencil), then the mode/role-dependent top bar, then AIBanner (per the design, the banner sits BELOW the name/period/Save-Report row, not above it),
 * a GroupByControl/ColumnManager control row (edit mode only — view mode goes straight from the top
 * bar to the table, as the design renders it), and the grouped `ReportTable` — or, when the fetch
 * matched zero rows, `EmptyReportState` in its place, explaining the run and naming the active
 * conditions instead of a bare empty grid, or `ReportErrorState` when the fetch failed outright
 * (which is not the same thing as matching nothing). This
 * is the surface BODY only — no modal chrome (see Task 9). Row data and its grouping/table state
 * all come from `useReportBuilder()`; grouping is applied client-side via `useClientGrouping`
 * before handing rows to `ReportTable`. Mode switching is done solely via the "Save Report"
 * (edit→view) and "Edit Report" (view→edit) buttons in the top bars — no mode-toggle UI here. */
export function ReportBuilderSurface(): JSX.Element {
	const {
		definition,
		mode,
		tableState,
		updateTableState,
		toggleFavorite,
		rows,
		totalCount,
		cappedTotalEstimate,
		totalIsFloor,
		capped,
		grouping,
		hasFetched,
		fetchFailed,
		retryFetch,
	} = useReportBuilder();
	const {formatMessage} = useIntl();
	const [capBannerDismissed, setCapBannerDismissed] = useState(false);

	// Dismissal applies to the result on screen, not to the report forever. Editing the report
	// re-fetches (a new `definition` identity is what drives the fetch), and that new result can be
	// capped too — so a dismissal that survived would silently hide the truncation.
	useEffect(() => {
		setCapBannerDismissed(false);
	}, [definition]);

	const groupedRows = useClientGrouping(rows, grouping);

	const totalLabel = totalIsFloor ? `${totalCount}+` : String(totalCount);
	const capBanner =
		capped && !capBannerDismissed ? (
			<Alert
				type={Alert.TYPE.INFORMATION}
				text={formatMessage({id: 'ai_report_builder.row_cap_banner'}, {shown: cappedTotalEstimate, total: totalLabel})}
				onDismiss={() => setCapBannerDismissed(true)}
			/>
		) : null;

	return (
		<SurfaceWrapper data-cy={'ai-report-builder-surface'}>
			<ReportPageHeader />

			{mode === 'edit' ? <EditTopBar /> : <ViewTopBar />}

			<AIBanner />

			{mode === 'edit' ? (
				<FlexRow gap={'xl'} justifyContent={'flex-end'} alignItems={'flex-end'}>
					<GroupByControl />
					<ColumnManager />
				</FlexRow>
			) : null}

			{capBanner}

			{fetchFailed ? (
				<ReportErrorState onRetry={retryFetch} />
			) : hasFetched && rows.length === 0 ? (
				<EmptyReportState definition={definition} mode={mode} />
			) : (
				<ReportTable
					rows={groupedRows}
					definition={definition}
					tableState={tableState}
					onTableStateChange={updateTableState}
					onToggleFavorite={toggleFavorite}
				/>
			)}
		</SurfaceWrapper>
	);
}
